import React, { useState, useEffect } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";

const VIPScanner = () => {
    const [scanResults, setScanResults] = useState([]); // Store all scanned data
    const [lastScanned, setLastScanned] = useState(""); // Most recent scanned item
    const [cameraId, setCameraId] = useState(null); // Store the back camera ID
    const [scanner, setScanner] = useState(null); // Store the Html5Qrcode scanner instance
    const [showTick, setShowTick] = useState(false); // Show success tick
    const [showCross, setShowCross] = useState(false); // Show error cross
    const [lastScanTime, setLastScanTime] = useState(0); // Prevent duplicate scans
    const [errorMessage, setErrorMessage] = useState(null); // Display error messages

    const ERROR_MESSAGES = {
        INVALID_QR: "Invalid QR code format.",
        INVALID_DATE: "Invalid date in QR code.",
        INVALID_COUNT: "Invalid count in QR code.",
        ALREADY_SCANNED: "This QR code has already been scanned.",
        SAVE_ERROR: "An error occurred while saving the scan data.",
    };

    const QR_CODE_REGEX = /^Date:\s(\d{2}-\d{2}-\d{4}),\sCount:\s(\d+)$/;

    useEffect(() => {
        // Initialize scanner and fetch cameras
        Html5Qrcode.getCameras()
            .then((devices) => {
                if (devices.length > 0) {
                    const backCamera = devices.find((device) =>
                        device.label.toLowerCase().includes("back")
                    );
                    setCameraId(backCamera ? backCamera.id : devices[0].id);
                } else {
                    console.error("No cameras found!");
                }
            })
            .catch((err) => console.error("Error fetching cameras:", err));
    }, []);

    useEffect(() => {
        if (cameraId) {
            const html5QrCode = new Html5Qrcode("qr-reader", {
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
            });

            html5QrCode
                .start(
                    cameraId,
                    { fps: 10, qrbox: { width: 300, height: 300 }, aspectRatio: 1.0 },
                    handleScanSuccess,
                    (error) => console.error("Scanning Error:", error)
                )
                .then(() => setScanner(html5QrCode))
                .catch((err) => console.error("Error starting scanner:", err));

            return () => {
                html5QrCode.stop().catch((err) =>
                    console.error("Error stopping scanner:", err)
                );
            };
        }
    }, [cameraId]);

    const handleScanSuccess = async (decodedText) => {
        const currentTime = Date.now();

        // Prevent duplicate scans within 2 seconds
        if (decodedText === lastScanned && currentTime - lastScanTime < 2000) {
            return;
        }

        const match = decodedText.match(QR_CODE_REGEX);
        if (!match) {
            showError(ERROR_MESSAGES.INVALID_QR);
            return;
        }

        const scannedDate = match[1];
        const scannedCount = parseInt(match[2], 10);

        if (!validateDate(scannedDate)) {
            showError(ERROR_MESSAGES.INVALID_DATE);
            return;
        }

        if (scannedCount <= 0) {
            showError(ERROR_MESSAGES.INVALID_COUNT);
            return;
        }

        setLastScanned(decodedText);
        setLastScanTime(currentTime);
        setScanResults((prevResults) => [
            ...prevResults,
            { date: scannedDate, count: scannedCount },
        ]);
        setShowTick(true);

        try {
            const response = await fetch("/api/scans", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ date: scannedDate, count: scannedCount }),
            });

            const data = await response.json();

            if (response.ok) {
                console.log("Scan data saved successfully");
            } else if (data.status === "already_scanned") {
                showError(ERROR_MESSAGES.ALREADY_SCANNED);
            } else {
                showError(data.message || ERROR_MESSAGES.SAVE_ERROR);
            }
        } catch (err) {
            console.error("Error saving scan data:", err);
            showError(ERROR_MESSAGES.SAVE_ERROR);
        }

        setTimeout(() => setShowTick(false), 1000);
    };

    const validateDate = (date) => {
        const [day, month, year] = date.split("-").map(Number);
        return (
            day >= 1 &&
            day <= 31 &&
            month >= 1 &&
            month <= 12 &&
            year >= 2000 &&
            year <= 2100
        );
    };

    const showError = (message) => {
        setShowCross(true);
        setErrorMessage(message);
        setTimeout(() => {
            setShowCross(false);
            setErrorMessage(null);
        }, 2000);
    };

    return (
        <div style={styles.container}>
            <img src="/khasdar.jpg" alt="Logo" style={styles.logo} />
            <h1 style={styles.title}>VVIP Car Entry Scanner</h1>
            <div id="qr-reader" style={styles.qrReader}></div>

            {showTick && <div style={styles.tick}>✅</div>}
            {showCross && <div style={styles.cross}>❌</div>}
            {errorMessage && <div style={styles.errorMessage}>{errorMessage}</div>}
            {lastScanned && (
                <div style={styles.lastScanned}>
                    <strong>Scanned:</strong> {lastScanned}
                </div>
            )}
        </div>
    );
};

const styles = {
    container: {
        textAlign: "center",
        fontFamily: "Arial, sans-serif",
        padding: "20px",
        background: "linear-gradient(135deg, #5ee7df, #b490ca)",
        color: "#fff",
        minHeight: "100vh",
    },
    title: {
        fontSize: "24px",
        fontWeight: "bold",
        marginBottom: "20px",
    },
    logo: {
        width: "100px",
        marginBottom: "20px",
    },
    qrReader: {
        width: "100%",
        maxWidth: "400px",
        margin: "auto",
        border: "5px solid #fff",
        borderRadius: "10px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
    },
    tick: {
        fontSize: "48px",
        color: "#28a745",
        fontWeight: "bold",
        margin: "20px 0",
    },
    cross: {
        fontSize: "48px",
        color: "red",
        fontWeight: "bold",
        margin: "20px 0",
    },
    errorMessage: {
        color: "red",
        fontSize: "18px",
        margin: "10px 0",
    },
    lastScanned: {
        fontSize: "18px",
        color: "#fff",
        marginTop: "10px",
    },
};

export default VIPScanner;
